import { Lock, Cloud, Users } from 'lucide-react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AnimatedSection } from './components/AnimatedSection';
import { FeatureCard } from './components/FeatureCard';
import { ProductCard } from './components/ProductCard';
import { theme } from './components/theme';


 const handleClick = (link) => {
    if (link.startsWith('http')) {
      // External URL — open in same tab
      window.location.href = link;
      // or window.open(link, '_blank') if you prefer new tab
    } else if (link.startsWith('/')) {
      // Internal routes
      window.open(link, '_self');
    }
  };


const products = [
  {
    name: 'Diff Insight',
    description:
      'Advanced data comparison and analysis tool that provides actionable insights while maintaining enterprise-grade security standards.',
    link: 'https://diffinsight.mind-mesh.com',
    // link: '/products/diffinsight',
    icon: '🔍',
    features: ['Real-time Analysis', 'Zero Data Storage', 'Enterprise Security'],
  },
  {
    name: 'RCA Migration',
    description:
      'Seamless migration solution between CPQ and RCA systems with automated workflows and comprehensive data integrity checks.',
    link: '/RcaMigration',
    icon: '🔄',
    features: ['Automated Migration', 'Data Integrity', 'Zero Downtime'],
  },
  {
    name: 'CRM Platform',
    description:
      'Comprehensive customer relationship management platform designed to streamline operations and drive business growth.',
    link: '/CRM',
    icon: '📊',
    features: ['Sales Pipeline', 'Customer Analytics', 'Team Collaboration'],
  },
];

function CRMLandingPage() {
  const handleNavigate = (path) => {
    if (path.startsWith('http')) {
      window.location.href = path;
    } else if (path.startsWith('/')) {
      window.open(path, '_self');
    } else {
      const element = document.getElementById(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };


  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.neutral.white,
        fontFamily: theme.typography.fontFamily.base,
      }}
    >
      <Header onNavigate={handleNavigate} />

      <main style={{ paddingTop: '72px' }}>
        <section
          id="hero"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.accent.peach} 0%, ${theme.colors.neutral.white} 100%)`,
            padding: `${theme.spacing['5xl']} ${theme.spacing.lg} ${theme.spacing['4xl']}`,
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <h1
                style={{
                  fontSize: theme.typography.fontSize['6xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.primary.navy,
                  marginBottom: theme.spacing.lg,
                  lineHeight: theme.typography.lineHeight.tight,
                }}
              >
                Transform Your Business with
                <br />
                <span style={{ color: theme.colors.primary.orange }}>Enterprise Solutions</span>
              </h1>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <p
                style={{
                  fontSize: theme.typography.fontSize.xl,
                  color: theme.colors.primary.blue,
                  maxWidth: '900px',
                  margin: `0 auto ${theme.spacing['2xl']}`,
                  lineHeight: theme.typography.lineHeight.relaxed,
                  fontWeight: theme.typography.fontWeight.regular,
                }}
              >
                Streamline operations, enhance productivity, and accelerate growth with our
                comprehensive suite of enterprise-grade tools designed for modern businesses.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200}>
              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.md,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  onClick={() => scrollToSection('products')}
                  style={{
                    padding: '16px 32px',
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.neutral.white,
                    backgroundColor: theme.colors.primary.orange,
                    border: 'none',
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer',
                    transition: `all ${theme.transitions.base}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.orangeLight;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = theme.shadows.xl;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.orange;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Explore Solutions
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  style={{
                    padding: '16px 32px',
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.primary.navy,
                    backgroundColor: 'transparent',
                    border: `2px solid ${theme.colors.primary.navy}`,
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer',
                    transition: `all ${theme.transitions.base}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.navy;
                    e.currentTarget.style.color = theme.colors.neutral.white;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = theme.colors.primary.navy;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Learn More
                </button>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section
          id="features"
          style={{
            backgroundColor: theme.colors.neutral.gray50,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <div style={{ textAlign: 'center', marginBottom: theme.spacing['3xl'] }}>
                <h2
                  style={{
                    fontSize: theme.typography.fontSize['4xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.colors.primary.navy,
                    marginBottom: theme.spacing.md,
                    lineHeight: theme.typography.lineHeight.tight,
                  }}
                >
                  Why Choose Aptify?
                </h2>
                <p
                  style={{
                    fontSize: theme.typography.fontSize.lg,
                    color: theme.colors.primary.blue,
                    maxWidth: '600px',
                    margin: '0 auto',
                    lineHeight: theme.typography.lineHeight.relaxed,
                  }}
                >
                  Built for enterprise scale with security, reliability, and performance at its
                  core.
                </p>
              </div>
            </AnimatedSection>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: theme.spacing.xl,
              }}
            >
              <AnimatedSection animation="fade-up" delay={0}>
                <FeatureCard
                  title="Enterprise Security"
                  description="SOC 2 compliant with end-to-end encryption. Your data never leaves your control with our zero-trust architecture."
                  icon={Lock}
                  iconBgColor={theme.colors.accent.lightOrange}
                  iconColor={theme.colors.primary.orange}
                />
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                <FeatureCard
                  title="Seamless Integration"
                  description="Connect with 500+ business applications through our robust API ecosystem and pre-built integrations."
                  icon={Cloud}
                  iconBgColor={theme.colors.accent.lightBlue}
                  iconColor={theme.colors.primary.blue}
                />
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200}>
                <FeatureCard
                  title="24/7 Expert Support"
                  description="Dedicated customer success team with average response time under 2 hours and 99.9% uptime SLA."
                  icon={Users}
                  iconBgColor={theme.colors.accent.lightGreen}
                  iconColor={theme.colors.semantic.success}
                />
              </AnimatedSection>
            </div>
          </div>
        </section>

        <section
          id="products"
          style={{
            backgroundColor: theme.colors.neutral.white,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <div style={{ textAlign: 'center', marginBottom: theme.spacing['3xl'] }}>
                <h2
                  style={{
                    fontSize: theme.typography.fontSize['4xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.colors.primary.navy,
                    marginBottom: theme.spacing.md,
                    lineHeight: theme.typography.lineHeight.tight,
                  }}
                >
                  Our Solutions Portfolio
                </h2>
                <p
                  style={{
                    fontSize: theme.typography.fontSize.lg,
                    color: theme.colors.primary.blue,
                    maxWidth: '700px',
                    margin: '0 auto',
                    lineHeight: theme.typography.lineHeight.relaxed,
                  }}
                >
                  Comprehensive tools designed to address every aspect of your business operations
                  with enterprise-grade reliability.
                </p>
              </div>
            </AnimatedSection>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: theme.spacing.xl,
              }}
            >
              {products.map((product, index) => (
                <AnimatedSection key={product.name} animation="fade-up" delay={index * 100}>
                  <ProductCard
                    name={product.name}
                    description={product.description}
                    icon={product.icon}
                    features={product.features}
                    link={product.link}
                    handleClick={handleClick}
                  />
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        <section
          id="pricing"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.primary.navy} 0%, ${theme.colors.primary.blue} 100%)`,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <h2
                style={{
                  fontSize: theme.typography.fontSize['4xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.neutral.white,
                  marginBottom: theme.spacing.md,
                  lineHeight: theme.typography.lineHeight.tight,
                }}
              >
                Ready to Transform Your Business?
              </h2>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <p
                style={{
                  fontSize: theme.typography.fontSize.lg,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: theme.spacing['2xl'],
                  lineHeight: theme.typography.lineHeight.relaxed,
                }}
              >
                Join thousands of organizations worldwide who trust Aptify to power their digital
                transformation. Start your free trial today and experience the difference.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200}>
              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.md,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  onClick={() => scrollToSection('products')}
                  style={{
                    padding: '16px 32px',
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.neutral.white,
                    backgroundColor: theme.colors.primary.orange,
                    border: 'none',
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer',
                    transition: `all ${theme.transitions.base}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.orangeLight;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = theme.shadows.xl;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.orange;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Get Started Now
                </button>
                <button
                  onClick={() => scrollToSection('hero')}
                  style={{
                    padding: '16px 32px',
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.neutral.white,
                    backgroundColor: 'transparent',
                    border: `2px solid ${theme.colors.neutral.white}`,
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer',
                    transition: `all ${theme.transitions.base}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.neutral.white;
                    e.currentTarget.style.color = theme.colors.primary.navy;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = theme.colors.neutral.white;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Back to Top
                </button>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
}

export default CRMLandingPage;
